import { BuildingEntityNodeBase } from "building-common/buildingEntityNodes/buildingEntityNode";
import type { GeometryQuery } from "building-common/geometryQuery";
import { emptyGeometryQuery, faceGeometryQueryNew, planeGeometryQueryNew } from "building-common/geometryQuery";
import { wallOpeningDepth, wallOpeningOpening2, wallOpeningPlane } from "building-common/openings/openingQuery";
import type { DoorUnit } from "building-types/doors/doorTypes";
import { FRONT } from "building-types/openings/openingTypes";
import type { WallOpening } from "building-types/openings/wallOpeningTypes";
import type { WindowUnit } from "building-types/windows/windowTypes";
import type { WorldInches } from "common/types/nominalNumbersTypes";
import { worldInchesNew } from "common/utils/nominalNumbers";
import { faceNew, faceRepairLoopDirections } from "geom/brep";
import { extrudePolyLine2OnPlane } from "geom/extrude";
import { polyLine2AABB2 } from "geom/polyLine2";
import { polyLine2New } from "geom/polyLine2Types";

export default class OpeningUnitNode extends BuildingEntityNodeBase<WindowUnit | DoorUnit> {
  private _frontPlane: GeometryQuery | undefined;
  private _frontFace: GeometryQuery | undefined;
  private _bottomFaceAtSill: GeometryQuery | undefined;

  /** The plane on the inside of the host wall */
  get frontPlane(): GeometryQuery {
    if (!this._frontPlane) {
      const opening = this.getWallOpening();
      this._frontPlane = opening ? planeGeometryQueryNew(wallOpeningPlane(opening, FRONT)) : emptyGeometryQuery;
    }
    return this._frontPlane;
  }

  /** A face on the inside of the host wall representing the opening's profile */
  get frontFace(): GeometryQuery {
    if (!this._frontFace) {
      const opening = this.getWallOpening();
      if (opening) {
        this._frontFace = faceGeometryQueryNew(
          faceRepairLoopDirections(
            faceNew([wallOpeningOpening2(opening, FRONT, false)], wallOpeningPlane(opening, FRONT)),
          ),
        );
      } else {
        this._frontFace = emptyGeometryQuery;
      }
    }
    return this._frontFace;
  }

  /** The bottom face of the opening volume */
  get bottomFaceAtSill(): GeometryQuery {
    if (!this._bottomFaceAtSill) {
      const opening = this.getWallOpening();
      if (opening) {
        const frontPlane = wallOpeningPlane(opening, FRONT);
        const windowDepth = wallOpeningDepth(opening);

        const openingAABB = polyLine2AABB2(wallOpeningOpening2(opening, "FRONT", false));
        const openingBottomEdge = polyLine2New([
          openingAABB.minX,
          openingAABB.minY,
          openingAABB.maxX,
          openingAABB.minY,
        ]);
        const [openingBottomFace] = extrudePolyLine2OnPlane(frontPlane, openingBottomEdge, -windowDepth);
        this._bottomFaceAtSill = faceGeometryQueryNew(openingBottomFace);
      } else {
        this._bottomFaceAtSill = emptyGeometryQuery;
      }
    }
    return this._bottomFaceAtSill;
  }

  constructor(entity: WindowUnit | DoorUnit) {
    super(entity);
  }

  private getWallOpening(): WallOpening | undefined {
    switch (this.entity.entityType) {
      case "WINDOW":
        return this.entity.windowPlacement.opening;
      case "DOOR":
        return this.entity.doorPlacement.opening;
      default:
        return undefined;
    }
  }

  getWallOpeningDepth(): WorldInches {
    const opening = this.getWallOpening();
    return opening ? wallOpeningDepth(opening) : worldInchesNew(0);
  }
}
